import { useTranslations } from '@vocab/react';
import {
  Box,
  Dialog,
  Text,
  Stack,
  Button,
  ButtonLink,
  IconNewWindow,
  TextLink,
} from 'braid-design-system';
import { useEffect } from 'react';
import { useEffectOnce } from 'react-use';

import type {
  RequiredIdentityVerification,
  RequiredRtwVerification,
} from 'src/__generated__/graphql';
import { useConfig } from 'src/context/config';
import { Page } from 'src/context/tracking';
import { useJobId } from 'src/context/useJobId';
import { useEnhancedHubble } from 'src/hooks/useEnhancedHubble';
import { navigateToUrl } from 'src/utils';

import translations from './.vocab';
import { RightToWorkLogo } from './RightToWorkLogo';
import { useLearnMoreLink } from './useLearnMoreLink';

interface Props {
  setIsSerialApplicant: (value: boolean) => void;
  verifications: Array<RequiredRtwVerification | RequiredIdentityVerification>;
}

export const VerificationDialog = ({
  setIsSerialApplicant,
  verifications,
}: Props) => {
  const { t } = useTranslations(translations);
  const hubble = useEnhancedHubble();
  const { config } = useConfig();
  const { jobId } = useJobId();
  const learnMoreLink = useLearnMoreLink();

  const VERIFICATION_CONFIG = {
    RequiredRtwVerification: {
      trackingName: 'verify right to work',
      LogoComponent: RightToWorkLogo,
      getLabel: (country: string) => t('Right to work', { country }),
    },
    // TODO: add other verification types here as they arise, for example:
    // RequiredIdentityVerification: {
    //   trackingName: 'verify identity',
    //   LogoComponent: RightToWorkLogo,
    //   getLabel: (country: string) => t('Right to work', { country }),
    // },
  } as const;

  const validVerifications = verifications.filter(
    ({ __typename }) => __typename && __typename in VERIFICATION_CONFIG,
  );

  // Impression tracking
  useEffectOnce(() => {
    if (config.ZONE && config.VOCAB_LANGUAGE && validVerifications.length > 0) {
      validVerifications.forEach(({ __typename }) => {
        const verificationConfig =
          VERIFICATION_CONFIG[__typename as keyof typeof VERIFICATION_CONFIG];
        hubble.trackEvent('component_viewed', {
          currentPageSection:
            VERIFICATION_CONFIG.RequiredRtwVerification.trackingName,
          component: {
            type: 'card',
            name: verificationConfig.trackingName,
          },
          displayLanguage: config.VOCAB_LANGUAGE,
          zone: config.ZONE,
          jobId,
        });
      });
    }
  });

  const trackSerialApplicantClick = (componentName: string) => {
    hubble.trackEvent('component_pressed', {
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection:
        VERIFICATION_CONFIG.RequiredRtwVerification.trackingName,
      component: {
        type: 'button',
        name: componentName,
      },
      jobId,
    });
  };

  const trackLearnMoreClick = () => {
    hubble.trackEvent('component_pressed', {
      currentPage: Page.DOCUMENT_PAGE,
      currentPageSection:
        VERIFICATION_CONFIG.RequiredRtwVerification.trackingName,
      component: {
        type: 'button',
        name: 'verify learn more',
      },
      jobId,
    });
  };

  // Single timeout to blur the dialog title after it gets focus
  useEffect(() => {
    const timer = setTimeout(() => {
      const activeElement = document.activeElement as HTMLElement;
      activeElement?.blur();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const renderVerification = ({
    __typename,
    countryLabel,
    nudgeUrl,
  }: Props['verifications'][0]) => {
    const verificationConfig =
      VERIFICATION_CONFIG[__typename as keyof typeof VERIFICATION_CONFIG];
    const { LogoComponent, getLabel } = verificationConfig;

    return (
      <>
        <LogoComponent />
        <Box paddingBottom="small">
          <Text weight="strong" size="large">
            {getLabel(countryLabel.label)}
          </Text>
        </Box>
        <ButtonLink
          variant="solid"
          tone="formAccent"
          icon={<IconNewWindow />}
          iconPosition="trailing"
          href={nudgeUrl}
          rel="noopener noreferrer"
          target="_blank"
          onClick={() => trackSerialApplicantClick('verify now')}
        >
          {t('Verify now')}
        </ButtonLink>
      </>
    );
  };

  if (validVerifications.length === 0) {
    return null;
  }

  return (
    <Dialog
      id="verification_dialog"
      open={true}
      title={t('Verify your work')}
      onClose={() => {
        trackSerialApplicantClick('dismiss');
        navigateToUrl('./');
      }}
      closeLabel={t('Close')}
      width="small"
    >
      <Text>
        {t('Verifying helps you', {
          Link: (v) => (
            <TextLink
              hitArea="large"
              href={learnMoreLink}
              data={{
                automation: 'serial-applicant-learn-more',
              }}
              target="_blank"
              onClick={trackLearnMoreClick}
            >
              {v}
            </TextLink>
          ),
        })}
      </Text>
      <Stack space="small" align="center">
        {validVerifications.map(renderVerification)}
        <Button
          variant="transparent"
          tone="formAccent"
          onClick={() => {
            trackSerialApplicantClick('require sponsorship');
            setIsSerialApplicant(false);
          }}
        >
          {t('I require sponsorship')}
        </Button>
      </Stack>
      <Text align="center" size="small" tone="secondary">
        {t('Restart this job', {
          country: validVerifications[0].countryLabel.label,
          brand: config.BRAND,
        })}
      </Text>
    </Dialog>
  );
};
